/**
 * HTTP client for the backend API with a demo-mode short-circuit.
 *
 * In demo mode (``VITE_ANC_DEMO=1`` or ``localStorage.anc.demo=1``)
 * the client returns synthetic fixtures so the SPA renders without a
 * running backend.  In normal mode it forwards to the FastAPI
 * service with the OIDC bearer token.
 */
import { ANC_API_BASE_URL } from "../config";
import { getAccessToken } from "../auth/oidc";
import { mockApi } from "./mock";
import { tryAdapter } from "./adapters";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    public readonly url: string,
  ) {
    super(`API ${status} on ${url}`);
    this.name = "ApiError";
  }
}

export interface ApiRequestInit extends RequestInit {
  /** Skip the automatic Authorization header. */
  anonymous?: boolean;
}

function isDemoMode(): boolean {
  if (typeof window === "undefined") return false;
  if (import.meta.env.VITE_ANC_DEMO === "1") return true;
  try {
    return window.localStorage.getItem("anc.demo") === "1";
  } catch {
    return false;
  }
}

export async function api<T = unknown>(
  path: string,
  init: ApiRequestInit = {},
): Promise<T> {
  // Capa de fusión: si hay backend real (rf-api) y existe un adaptador
  // para esta ruta, úsalo (con fallback a mock ante error).
  const adapted = await tryAdapter<T>(path, init);
  if (adapted.handled) {
    return adapted.data as T;
  }
  if (isDemoMode()) {
    return mockApi<T>(path, init);
  }
  const url = path.startsWith("http") ? path : `${ANC_API_BASE_URL}${path}`;
  const headers = new Headers(init.headers);
  if (!init.anonymous) {
    const token = await getAccessToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(url, { ...init, headers });
  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!response.ok) {
    throw new ApiError(response.status, body, url);
  }
  return body as T;
}
